import React, { useContext, useEffect, useRef, useState } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    IconButton,
    Grid,
    Card,
    CardContent,
    TextField,
    FormControl,
    Autocomplete,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import "../../assets/styles/AddModal.scss";
import { CommonContext } from "../../context/CommonContext";
import { ProductsContext } from "../../context/ProductsContext";
import commonApi from "../../utils/api";
import AlertMessage from "../common/AlertMessage";

const initialFormState = {
    merchant_id: "",
    name: "",
    sku_code: "",
    serial_no: "",
    brand_id: "",
    category_id: "",
    subcategory_id: "",
    invoice_value: "",
    length_cm: "",
    breadth_cm: "",
    height_cm: "",
    volumetric_weight: "",
    dead_weight: "",
    product_unit: "",
    status: "1",
    gst_percentage: "",
};

const AddModal = () => {
    const { modalAlert, setModalAlert, setAlertMessage, setSeverity, merchant, user } =
        useContext(CommonContext);
    const {
        addModal,
        handleCloseModal,
        onLoadApi,
        viewData,
        isEdit,
        setIsLoading,
        locationState,
    } = useContext(ProductsContext);

    const [formDataTemp, setFormDataTemp] = useState(initialFormState);
    const [formErrors, setFormErrors] = useState({});
    const [brandOptions, setBrandOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [subcategoryOptions, setSubcategoryOptions] = useState([]);
    const modalContentRef = useRef(null);

    useEffect(() => {
        if (viewData && isEdit) {
            setFormDataTemp({ ...viewData });
        } else {
            setFormDataTemp(initialFormState);
        }
        setFormErrors({});
    }, [viewData, isEdit]);

    // Fetch brands when merchant_id changes
    useEffect(() => {
        const merchantId = locationState?.id || formDataTemp.merchant_id || (user?.user_type === "Merchant" ? user?.id : null);

        if (merchantId) {
            commonApi.post('/merchant-brand/listForDropDown', { merchant_id: merchantId })
                .then((res) => {
                    if (res?.data?.s === 1) {
                        setBrandOptions(res?.data?.data || []);
                    } else {
                        setBrandOptions([]);
                    }
                })
                .catch((err) => {
                    console.error("Error fetching brands:", err);
                    setBrandOptions([]);
                });
        } else {
            setBrandOptions([]);
        }
    }, [formDataTemp.merchant_id, locationState, user]);

    // Fetch categories when merchant_id changes
    useEffect(() => {
        const merchantId = locationState?.id || formDataTemp.merchant_id || (user?.user_type === "Merchant" ? user?.id : null);

        if (merchantId) {
            commonApi.post('/merchant-category/listForDropDown', { merchant_id: merchantId })
                .then((res) => {
                    if (res?.data?.s === 1) {
                        setCategoryOptions(res?.data?.data || []);
                    } else {
                        setCategoryOptions([]);
                    }
                })
                .catch((err) => {
                    console.error("Error fetching categories:", err);
                    setCategoryOptions([]);
                });
        } else {
            setCategoryOptions([]);
        }
    }, [formDataTemp.merchant_id, locationState, user]);

    // Fetch subcategories when merchant_id and category_id changes
    useEffect(() => {
        const merchantId = locationState?.id || formDataTemp.merchant_id || (user?.user_type === "Merchant" ? user?.id : null);

        if (merchantId && formDataTemp.category_id) {
            commonApi.post('/merchant-subcategory/listForDropDown', {
                merchant_id: merchantId,
                category_id: formDataTemp.category_id
            })
                .then((res) => {
                    if (res?.data?.s === 1) {
                        setSubcategoryOptions(res?.data?.data || []);
                    } else {
                        setSubcategoryOptions([]);
                    }
                })
                .catch((err) => {
                    console.error("Error fetching subcategories:", err);
                    setSubcategoryOptions([]);
                });
        } else {
            setSubcategoryOptions([]);
        }
    }, [formDataTemp.merchant_id, formDataTemp.category_id, locationState, user]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormDataTemp((prev) => ({ ...prev, [name]: value }));
    };

    const validateSingle = (name, value) => {
        switch (name) {
            case "merchant_id":
                return (!locationState && user?.user_type !== "Merchant" && !value)
                    ? "Merchant is required"
                    : "";
            case "name":
                return value ? "" : "Product Name is required";
            case "sku_code":
                return value ? "" : "SKU Code is required";
            case "brand_id":
                return value ? "" : "Brand is required";
            case "category_id":
                return value ? "" : "Category is required";
            case "subcategory_id":
                return value ? "" : "Subcategory is required";
            default:
                return "";
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        const err = validateSingle(name, formDataTemp[name]);
        setFormErrors((prev) => ({ ...prev, [name]: err }));
    };

    const validateForm = () => {
        const errors = {};
        Object.keys(initialFormState).forEach((key) => {
            const err = validateSingle(key, formDataTemp[key]);
            if (err) errors[key] = err;
        });
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);
        if (locationState) {
            formDataTemp.merchant_id = locationState?.id
        } else if (user?.user_type === "Merchant") {
            formDataTemp.merchant_id = user?.id
        }
        const url = isEdit ? "/merchant-product/edit" : "/merchant-product/add";

        try {
            const res = await commonApi.post(url, formDataTemp);
            setModalAlert(true);
            modalContentRef.current.scrollTo({ top: 0, behavior: "smooth" });

            if (res?.data?.s === 1) {
                setAlertMessage(res?.data?.msg || "Data saved successfully.");
                setSeverity("success");
                setFormDataTemp(initialFormState);
                setTimeout(() => {
                    handleCloseModal();
                    onLoadApi();
                }, 1500);
            } else {
                setAlertMessage(res?.data?.msg || "Something went wrong.");
                setSeverity("error");
            }
        } catch (err) {
            modalContentRef.current.scrollTo({ top: 0, behavior: "smooth" });
            setModalAlert(true);
            setAlertMessage(err?.message || "Error submitting form.");
            setSeverity("error");
            console.error("Error submitting form:", err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal open={addModal}>
            <Box className="add-modal">
                <Box className="modal-header">
                    <Typography variant="h6">
                        {isEdit ? "Edit" : "Add"} Product {locationState?.company_name && <small>for {locationState?.company_name}</small>}
                    </Typography>
                    <IconButton onClick={handleCloseModal} size="small">
                        <Close />
                    </IconButton>
                </Box>

                <Box className="content-body" ref={modalContentRef}>
                    {modalAlert && <AlertMessage />}

                    <form className="custom-form" onSubmit={handleSubmit} noValidate>
                        <Card className="form-section">
                            <CardContent>
                                <Grid container spacing={2}>
                                    {(!locationState && user?.user_type !== "Merchant") && (
                                        <Grid item size={{ xs: 6, md: 6 }}>
                                            <Autocomplete
                                                options={merchant ?? []}
                                                getOptionLabel={(option) => option.company_name}
                                                value={merchant.find(item => item.id === formDataTemp.merchant_id) || null}
                                                onChange={(event, newValue) => {
                                                    handleChange({
                                                        target: {
                                                            name: 'merchant_id',
                                                            value: newValue ? newValue.id : ''
                                                        }
                                                    });
                                                }}
                                                onBlur={() => {
                                                    handleBlur({
                                                        target: {
                                                            name: 'merchant_id',
                                                            value: formDataTemp.merchant_id
                                                        }
                                                    });
                                                }}
                                                sx={{ '& .MuiInputBase-root': { paddingTop: '0', paddingBottom: '0' }, margin: '0.5rem 0' }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Select Merchant"
                                                        variant="outlined"
                                                        error={!!formErrors.merchant_id}
                                                        helperText={formErrors.merchant_id}
                                                        fullWidth
                                                    />
                                                )}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                            />
                                        </Grid>
                                    )}

                                    {/* Product Name */}
                                    <Grid item size={{ xs: 6, md: 6 }}>
                                        <TextField
                                            label="Product Name"
                                            name="name"
                                            value={formDataTemp.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            size="small"
                                            error={!!formErrors.name}
                                            helperText={formErrors.name}
                                        />
                                    </Grid>

                                    {/* SKU Code */}
                                    <Grid item size={{ xs: 6, md: 6 }}>
                                        <TextField
                                            label="SKU Code"
                                            name="sku_code"
                                            value={formDataTemp.sku_code}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            size="small"
                                            error={!!formErrors.sku_code}
                                            helperText={formErrors.sku_code}
                                        />
                                    </Grid>

                                    {/* Serial No */}
                                    <Grid item size={{ xs: 6, md: 6 }}>
                                        <TextField
                                            label="Serial No"
                                            name="serial_no"
                                            value={formDataTemp.serial_no}
                                            onChange={handleChange}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>

                                    {/* Brand Dropdown */}
                                    <Grid item size={{ xs: 6, md: 6 }}>
                                        <Autocomplete
                                            options={brandOptions}
                                            getOptionLabel={(option) => option.name}
                                            value={brandOptions.find(item => item.id === formDataTemp.brand_id) || null}
                                            onChange={(event, newValue) => {
                                                handleChange({
                                                    target: {
                                                        name: 'brand_id',
                                                        value: newValue ? newValue.id : ''
                                                    }
                                                });
                                            }}
                                            onBlur={() => {
                                                handleBlur({
                                                    target: {
                                                        name: 'brand_id',
                                                        value: formDataTemp.brand_id
                                                    }
                                                });
                                            }}
                                            sx={{ '& .MuiInputBase-root': { paddingTop: '0', paddingBottom: '0' }, margin: '0.5rem 0' }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select Brand"
                                                    variant="outlined"
                                                    error={!!formErrors.brand_id}
                                                    helperText={formErrors.brand_id}
                                                    fullWidth
                                                />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                        />
                                    </Grid>

                                    {/* Category Dropdown */}
                                    <Grid item size={{ xs: 6, md: 6 }}>
                                        <Autocomplete
                                            options={categoryOptions}
                                            getOptionLabel={(option) => option.name}
                                            value={categoryOptions.find(item => item.id === formDataTemp.category_id) || null}
                                            onChange={(event, newValue) => {
                                                handleChange({
                                                    target: {
                                                        name: 'category_id',
                                                        value: newValue ? newValue.id : ''
                                                    }
                                                });
                                                // Reset subcategory when category changes
                                                setFormDataTemp(prev => ({ ...prev, subcategory_id: '' }));
                                            }}
                                            onBlur={() => {
                                                handleBlur({
                                                    target: {
                                                        name: 'category_id',
                                                        value: formDataTemp.category_id
                                                    }
                                                });
                                            }}
                                            sx={{ '& .MuiInputBase-root': { paddingTop: '0', paddingBottom: '0' }, margin: '0.5rem 0' }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select Category"
                                                    variant="outlined"
                                                    error={!!formErrors.category_id}
                                                    helperText={formErrors.category_id}
                                                    fullWidth
                                                />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                        />
                                    </Grid>

                                    {/* Subcategory Dropdown */}
                                    <Grid item size={{ xs: 6, md: 6 }}>
                                        <Autocomplete
                                            options={subcategoryOptions}
                                            getOptionLabel={(option) => option.name}
                                            value={subcategoryOptions.find(item => item.id === formDataTemp.subcategory_id) || null}
                                            onChange={(event, newValue) => {
                                                handleChange({
                                                    target: {
                                                        name: 'subcategory_id',
                                                        value: newValue ? newValue.id : ''
                                                    }
                                                });
                                            }}
                                            onBlur={() => {
                                                handleBlur({
                                                    target: {
                                                        name: 'subcategory_id',
                                                        value: formDataTemp.subcategory_id
                                                    }
                                                });
                                            }}
                                            sx={{ '& .MuiInputBase-root': { paddingTop: '0', paddingBottom: '0' }, margin: '0.5rem 0' }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select Subcategory"
                                                    variant="outlined"
                                                    error={!!formErrors.subcategory_id}
                                                    helperText={formErrors.subcategory_id}
                                                    fullWidth
                                                />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                        />
                                    </Grid>

                                    {/* Invoice Value */}
                                    <Grid item size={{ xs: 6, md: 6 }}>
                                        <TextField
                                            label="Invoice Value"
                                            name="invoice_value"
                                            type="number"
                                            value={formDataTemp.invoice_value}
                                            onChange={handleChange}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>

                                    {/* GST Percentage */}
                                    <Grid item size={{ xs: 6, md: 3 }}>
                                        <TextField
                                            label="GST Percentage"
                                            name="gst_percentage"
                                            type="number"
                                            value={formDataTemp.gst_percentage}
                                            onChange={handleChange}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>

                                    {/* Length (cm) */}
                                    <Grid item size={{ xs: 6, md: 3 }}>
                                        <TextField
                                            label="Length (cm)"
                                            name="length_cm"
                                            type="number"
                                            value={formDataTemp.length_cm}
                                            onChange={handleChange}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>

                                    {/* Breadth (cm) */}
                                    <Grid item size={{ xs: 6, md: 3 }}>
                                        <TextField
                                            label="Breadth (cm)"
                                            name="breadth_cm"
                                            type="number"
                                            value={formDataTemp.breadth_cm}
                                            onChange={handleChange}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>

                                    {/* Height (cm) */}
                                    <Grid item size={{ xs: 6, md: 3 }}>
                                        <TextField
                                            label="Height (cm)"
                                            name="height_cm"
                                            type="number"
                                            value={formDataTemp.height_cm}
                                            onChange={handleChange}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>

                                    {/* Volumetric Weight */}
                                    <Grid item size={{ xs: 6, md: 6 }}>
                                        <TextField
                                            label="Volumetric Weight"
                                            name="volumetric_weight"
                                            type="number"
                                            value={formDataTemp.volumetric_weight}
                                            onChange={handleChange}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>

                                    {/* Dead Weight */}
                                    <Grid item size={{ xs: 6, md: 6 }}>
                                        <TextField
                                            label="Dead Weight"
                                            name="dead_weight"
                                            type="number"
                                            value={formDataTemp.dead_weight}
                                            onChange={handleChange}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>

                                    {/* Product Unit */}
                                    <Grid item size={{ xs: 6, md: 6 }}>
                                        <TextField
                                            label="Product Unit"
                                            name="product_unit"
                                            value={formDataTemp.product_unit}
                                            onChange={handleChange}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>

                                    {/* Status */}
                                    <Grid item size={{ xs: 6, md: 6 }}>
                                        <FormControl component="fieldset">
                                            <RadioGroup
                                                name="status"
                                                value={formDataTemp.status}
                                                onChange={handleChange}
                                                row
                                            >
                                                <FormControlLabel
                                                    value="1"
                                                    control={<Radio />}
                                                    label="Active"
                                                />
                                                <FormControlLabel
                                                    value="0"
                                                    control={<Radio />}
                                                    label="Inactive"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </form>
                </Box>

                <Box className="form-actions">
                    <Button
                        variant="outlined"
                        onClick={handleCloseModal}
                        color="error"
                        type="button"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        type="submit"
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddModal;
